import type { OiaDocumentRef, OiaDocumentRole } from "@/features/oia/types"

export type SimulatorService = "oio" | "oia"

export interface PreparedOioInput {
  scn: { url: string; filename: string }
  reply: { url: string; filename: string } | null
  supporting: Array<{ url: string; filename: string }>
  exParte: boolean
}

export interface PreparedOiaInput {
  documents: OiaDocumentRef[]
  caseView: string
  templateId: string
  officeId?: string
  officerId?: string
}

interface SimulatorHandoffBase {
  userId: string
  caseId: string
  submissionId: string
}

export type SimulatorHandoff = SimulatorHandoffBase & (
  | { service: "oio"; prepared: PreparedOioInput }
  | { service: "oia"; prepared: PreparedOiaInput }
)

const PREFIX = "nitiassist:simulator-handoff:"
const claimedSubmissions = new Set<string>()

export function claimSimulatorHandoff(userId: string, caseId: string, submissionId: string): boolean {
  const key = `${userId}:${caseId}:${submissionId}`
  if (claimedSubmissions.has(key)) return false
  claimedSubmissions.add(key)
  return true
}

export function handoffKey(userId: string, caseId: string): string {
  return `${PREFIX}${encodeURIComponent(userId)}:${encodeURIComponent(caseId)}`
}

export function saveSimulatorHandoff(handoff: SimulatorHandoff): void {
  if (!isPreparedForService(handoff.service, handoff.prepared)) {
    throw new TypeError(`Invalid prepared input for ${handoff.service} simulator handoff`)
  }
  const value: SimulatorHandoff = handoff.service === "oio"
    ? { ...handoff, prepared: sanitizeOio(handoff.prepared) }
    : { ...handoff, prepared: sanitizeOia(handoff.prepared) }
  sessionStorage.setItem(handoffKey(handoff.userId, handoff.caseId), JSON.stringify(value))
}

export function readSimulatorHandoff(userId: string, caseId: string, service: SimulatorService): SimulatorHandoff | null {
  try {
    const raw = sessionStorage.getItem(handoffKey(userId, caseId))
    if (!raw) return null
    const value = JSON.parse(raw) as SimulatorHandoff
    if (value.userId !== userId || value.caseId !== caseId || value.service !== service || !value.submissionId) return null
    if (!isPreparedForService(value.service, value.prepared)) return null
    return value.service === "oio"
      ? { ...value, prepared: sanitizeOio(value.prepared) }
      : { ...value, prepared: sanitizeOia(value.prepared) }
  } catch {
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isDocumentRef(value: unknown): value is { url: string; filename: string } {
  return isRecord(value) && typeof value.url === "string" && typeof value.filename === "string"
}

function isPreparedForService(service: SimulatorService, value: unknown): value is PreparedOioInput | PreparedOiaInput {
  if (!isRecord(value)) return false
  if (service === "oio") {
    return isDocumentRef(value.scn)
      && (value.reply === null || isDocumentRef(value.reply))
      && Array.isArray(value.supporting)
      && value.supporting.every(isDocumentRef)
      && typeof value.exParte === "boolean"
  }

  const roles: OiaDocumentRole[] = ["impugned_order", "impugned_order_summary", "underlying_order", "appeal_form", "appeal_memo", "statement_of_facts", "review_order", "department_appeal_form", "additional_submission", "ph_record", "predeposit_proof", "scn_below", "reply_below", "department_remarks", "condonation_petition", "exhibit", "prior_appellate_order", "relied_judgment_copy", "other"]
  return Array.isArray(value.documents)
    && value.documents.every((document): document is OiaDocumentRef => isRecord(document)
      && typeof document.url === "string"
      && (document.filename === undefined || typeof document.filename === "string")
      && roles.includes(document.role_hint as OiaDocumentRole))
    && typeof value.caseView === "string"
    && typeof value.templateId === "string"
    && (value.officeId === undefined || typeof value.officeId === "string")
    && (value.officerId === undefined || typeof value.officerId === "string")
}

export function acknowledgeSimulatorHandoff(userId: string, caseId: string, submissionId: string): boolean {
  const key = handoffKey(userId, caseId)
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? "null") as SimulatorHandoff | null
    if (!value || value.userId !== userId || value.caseId !== caseId || value.submissionId !== submissionId) return false
    sessionStorage.removeItem(key)
    claimedSubmissions.delete(`${userId}:${caseId}:${submissionId}`)
    return true
  } catch {
    return false
  }
}

function sanitizeOio(input: PreparedOioInput): PreparedOioInput {
  const ref = (value: { url: string; filename: string }) => ({ url: value.url, filename: value.filename })
  return { scn: ref(input.scn), reply: input.reply ? ref(input.reply) : null, supporting: input.supporting.map(ref), exParte: input.exParte }
}

function sanitizeOia(input: PreparedOiaInput): PreparedOiaInput {
  return {
    documents: input.documents.map((document) => ({ url: document.url, filename: document.filename, role_hint: document.role_hint as OiaDocumentRole })),
    caseView: input.caseView,
    templateId: input.templateId,
    ...(input.officeId ? { officeId: input.officeId } : {}),
    ...(input.officerId ? { officerId: input.officerId } : {}),
  }
}
